import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { PreQualQuestion } from '../../../../../../../utils/types/JobOpenings';

interface QuestionItemProps {
  question: PreQualQuestion;
  index: number;
  typeLabel: string;
  onEdit: (question: PreQualQuestion) => void;
  onDelete: (key: string) => void;
}

export const QuestionItem = ({
  question,
  index,
  typeLabel,
  onEdit,
  onDelete,
}: QuestionItemProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0, flex: 1 }}>
        {/* Index Badge */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: 'grey.100',
            color: 'text.secondary',
            fontWeight: 700,
            fontSize: '0.8rem',
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          {index + 1}
        </Box>

        {/* Content */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.9rem',
                wordBreak: 'break-word',
              }}
            >
              {question.question}
            </Typography>

            <Chip
              size="small"
              label={typeLabel}
              sx={{
                height: 22,
                fontSize: '0.675rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                borderRadius: '6px',
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
              }}
            />
          </Box>

          {/* Choices Badges */}
          {question.choices && question.choices.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              {question.choices.map((choice, i) => (
                <Chip
                  key={i}
                  size="small"
                  label={choice}
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.675rem',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    borderColor: 'divider',
                    borderRadius: '4px',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        className="question-actions"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          opacity: { xs: 1, md: 0.4 },
          transition: 'opacity 0.2s ease',
        }}
      >
        <Tooltip title="Edit Question" arrow>
          <IconButton
            size="small"
            onClick={() => onEdit(question)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'primary.50',
              },
            }}
          >
            <EditRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete Question" arrow>
          <IconButton
            size="small"
            onClick={() => question.questionKey && onDelete(question.questionKey)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'error.main',
                bgcolor: 'error.50',
              },
            }}
          >
            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
